import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from 'helpers/api'
import { ITableData, Table } from 'components/table'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import { ConversationSummary, ConversationDetail, SKILL_LABELS } from './types'

const AdminConversations: React.FC = () => {
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [copying, setCopying] = useState(false)

  const fetchConversations = async () => {
    setLoading(true)
    try {
      const response = await api.getAdminConversations()
      setConversations(response.data)
    } catch (e) {
      displayErrorMessage(e)
    }
    setLoading(false)
  }

  useEffect(() => {
    document.title = 'Morta | Admin Conversations'
    fetchConversations()
  }, [])

  const tableData: Array<Record<string, ITableData>> = conversations.map((conv) => ({
    user: { label: conv.userEmail || conv.userName || '-', value: conv.userEmail || '' },
    messages: { label: conv.messageCount, value: conv.messageCount },
    created: {
      label: new Date(conv.createdAt).toLocaleString(),
      value: conv.createdAt
    },
    view: {
      label: (
        <Link className="text-xs underline text-blue" to={`/admin/conversations/${conv.publicId}`}>
          View
        </Link>
      ),
      value: ''
    }
  }))

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-[15px] p-2.5 bg-surface-muted">
        <div className="flex flex-col">
          <div className="italic text-muted text-sm">Recent Conversations (last 100)</div>
          <div className="text-xl">{conversations.length}</div>
        </div>
        <Button
          internalType="outline"
          className="text-xs"
          isLoading={copying}
          disabled={copying || conversations.length === 0}
          onClick={async () => {
            setCopying(true)
            try {
              const response = await api.getAdminConversationsExport()
              const details = response.data as ConversationDetail[]
              const text = details
                .map((conv) => {
                  const context = [
                    conv.hubContextId && `hub=${conv.hubContextId}`,
                    conv.viewContextId && `view=${conv.viewContextId}`,
                    conv.documentContextId && `doc=${conv.documentContextId}`
                  ]
                    .filter(Boolean)
                    .join(', ')
                  const header = `=== ${conv.userEmail || conv.userName || '-'} | ${conv.messageCount} messages | ${new Date(conv.createdAt).toLocaleString()}${context ? ` | ${context}` : ''} ===`
                  const messages = conv.messages.map((msg) => {
                    const lines: string[] = []
                    const role = msg.role === 'user' ? 'User' : 'Agent'
                    lines.push(`[${role} - ${new Date(msg.createdAt).toLocaleString()}]`)
                    if (msg.thinking) lines.push(`Thinking: ${msg.thinking}`)
                    if (msg.tools && msg.tools.length > 0) {
                      msg.tools.forEach((tool) => {
                        const skill = SKILL_LABELS[tool.skillId] || tool.skillId
                        const implemented = tool.implemented === true ? 'Yes' : tool.implemented === false ? 'No' : '-'
                        lines.push(`Tool: ${skill} (implemented: ${implemented})`)
                        if (tool.arguments) lines.push(`Arguments: ${JSON.stringify(tool.arguments)}`)
                        if (tool.output) lines.push(`Output: ${JSON.stringify(tool.output)}`)
                        if (tool.error?.message) lines.push(`Error: ${tool.error.message}`)
                      })
                    }
                    lines.push(msg.content)
                    if (msg.vote != null) lines.push(`Vote: ${msg.vote === 1 ? 'up' : 'down'}`)
                    if (msg.feedback) lines.push(`Feedback: ${msg.feedback}`)
                    return lines.join('\n')
                  })
                  return [header, ...messages].join('\n\n')
                })
                .join('\n\n---\n\n')
              await navigator.clipboard.writeText(text)
              setSnackbarMessage({ status: 'success', message: 'Conversations copied to clipboard' })
            } catch {
              displayErrorMessage('Failed to copy conversations. Please try again.')
            }
            setCopying(false)
          }}
        >
          Copy to Clipboard
        </Button>
      </div>

      <Table
        data={tableData}
        include={[
          { header: 'User', id: 'user' },
          { header: 'Messages', id: 'messages' },
          { header: 'Created', id: 'created' },
          { header: '', id: 'view' }
        ]}
        defaultSort={'created'}
        defaultSortAscending={false}
        loading={loading}
      />
    </div>
  )
}

export default AdminConversations
